// src/encounter/lib/encounter-vitals.ts
// PROD-3130 (Andrew, 9 Oct, dev frame v2): the PE vitals cards showed 15 vitals and the add-vitals form 9. ONE list
// now says which vitals the encounter has, in which order and under which name; the PE cards
// (`vitalsRowToBlockItems`) and the add-vitals form (`VitalsEditDialog`) both read it, so they cannot drift again.
//
// Order and set = the Flutter vitals list (`emptyVitalUiList`, ui_data.dart:1184-1368, mirrored by the appointment
// panel's VITAL_CATALOG) and the production PE row: the 8 core vitals, the PROD-1533 extended set, then the
// PROD-1274 context vitals. The name is the PE card's label, which is also the DS VitalsBlock's icon lookup
// (case-insensitive), so it is the one name both surfaces show.
import type { VitalKind, VitalsRowPatientType, VitalsRowVitals } from '@scripsteam/scrips-signal-ds';
import type { VitalsFormValue } from './to-vitals-request';

/** How the add-vitals form takes the vital. */
export type EncounterVitalEntry =
  /** One number field of the form. */
  | { kind: 'number'; field: keyof VitalsFormValue; unit: string; inputMode: 'decimal' | 'numeric'; ariaLabel: string }
  /** Systolic / diastolic. */
  | { kind: 'bp' }
  /** Computed from other vitals (BMI, waist-to-hip ratio); never typed. */
  | { kind: 'computed' }
  /** Its own capture form (distance / near, each eye). */
  | { kind: 'acuity' }
  /** Left / right level (the DS HearingTestEditor). */
  | { kind: 'hearing' }
  /** A date, or "Not menstruating", with the menstruating / breast-feeding flags (LmpEditor). */
  | { kind: 'lmp' };

export interface EncounterVital {
  key: keyof VitalsRowVitals;
  /** The name on the PE card AND the form tile. */
  label: string;
  /** The form tile's glyph (VITAL_KIND_TO_GLYPH) — the same icon the PE card draws. */
  kind: VitalKind;
  /** PE card: `always` draws a dash card when unread; `reading` only when recorded (head circumference: production's
   *  PE row never draws an empty one). The form always draws the tile — that is where a missing value is typed. */
  presence: 'always' | 'reading';
  /** PROD-1274: only for this patient type ("a tile that cannot apply to the patient is a meaning error"). */
  context?: VitalsRowPatientType;
  entry: EncounterVitalEntry;
}

const number = (field: keyof VitalsFormValue, unit: string, inputMode: 'decimal' | 'numeric', ariaLabel: string): EncounterVitalEntry => ({
  kind: 'number',
  field,
  unit,
  inputMode,
  ariaLabel,
});

export const ENCOUNTER_VITALS: readonly EncounterVital[] = [
  { key: 'temp', label: 'Temp', kind: 'temp', presence: 'always', entry: number('temp', '°C', 'decimal', 'Temperature (°C)') },
  { key: 'pulse', label: 'Pulse', kind: 'pulse', presence: 'always', entry: number('pulse', 'bpm', 'numeric', 'Heart rate (bpm)') },
  { key: 'bp', label: 'BP', kind: 'bp', presence: 'always', entry: { kind: 'bp' } },
  { key: 'respRate', label: 'Resp Rate', kind: 'resp', presence: 'always', entry: number('respRate', '/min', 'numeric', 'Resp. rate (/min)') },
  { key: 'o2Sat', label: 'O2 Sat', kind: 'o2', presence: 'always', entry: number('o2Sat', '%', 'numeric', 'O₂ sat (%)') },
  { key: 'height', label: 'Height', kind: 'height', presence: 'always', entry: number('height', 'cm', 'decimal', 'Height (cm)') },
  { key: 'weight', label: 'Weight', kind: 'weight', presence: 'always', entry: number('weight', 'kg', 'decimal', 'Weight (kg)') },
  { key: 'bmi', label: 'BMI', kind: 'bmi', presence: 'always', entry: { kind: 'computed' } },
  // PROD-1533 extended set: always drawn, a dash card when unread (production's PE row, measured 2026-09-25).
  { key: 'hipCirc', label: 'Hip Circumference', kind: 'hipCirc', presence: 'always', entry: number('hipCirc', 'cm', 'decimal', 'Hip circumference (cm)') },
  { key: 'waistCirc', label: 'Waist Circumference', kind: 'waistCirc', presence: 'always', entry: number('waistCirc', 'cm', 'decimal', 'Waist circumference (cm)') },
  { key: 'waistToHipRatio', label: 'Waist-to-Hip Ratio', kind: 'waistToHipRatio', presence: 'always', entry: { kind: 'computed' } },
  // The form shows "years" beside the field (desk, #673, 9 Oct: "Bioage has no unit"). Display only: the stored unit
  // stays as to-vitals-request.ts writes it, and the PE card shows the reading's own unit.
  { key: 'bioage', label: 'Bioage', kind: 'bioage', presence: 'always', entry: number('bioage', 'years', 'decimal', 'Bioage (years)') },
  { key: 'visualAcuity', label: 'Visual Acuity', kind: 'visualAcuity', presence: 'always', entry: { kind: 'acuity' } },
  { key: 'hearingTest', label: 'Hearing Test', kind: 'hearingTest', presence: 'always', entry: { kind: 'hearing' } },
  // PROD-1274 context vitals.
  { key: 'headCirc', label: 'Head Circumference', kind: 'headCirc', presence: 'reading', context: 'newborn', entry: number('headCirc', 'cm', 'decimal', 'Head circumference (cm)') },
  { key: 'lmp', label: 'LMP', kind: 'lmp', presence: 'always', context: 'female', entry: { kind: 'lmp' } },
];

/** The encounter's vitals for this patient, in order: the list both the PE cards and the add-vitals form draw. */
export function encounterVitalsFor(patientType?: VitalsRowPatientType): EncounterVital[] {
  return ENCOUNTER_VITALS.filter((v) => !v.context || v.context === patientType);
}
